import React, { useState, useEffect } from 'react';
import apiClient from '../api/apiClient';
import BalanceWidget from './BalanceWidget';
import Button from './ui/Button';
import Input from './ui/Input';
import Select from './ui/Select';
import { clearTransactionFieldError, fieldErrorFromTransactionResponse, transactionFieldErrors } from '../utils/transactionFormValidation';
import { localNowDateTime } from '../utils/formatDateTime';
import CategoryPicker from './ui/CategoryPicker';
import DatePicker from './ui/DatePicker';
import { useBalance } from '../context/BalanceContext';
import { TransactionModal } from './TransactionPage';

const getConvertedAmount = (amount, fromLocation, toLocation, currencies) => {
  const fromRate = Number(currencies.find((item) => item.code === fromLocation?.currency)?.rate_to_rub);
  const toRate = Number(currencies.find((item) => item.code === toLocation?.currency)?.rate_to_rub);
  if (!Number(amount) || !fromRate || !toRate) return '';
  return (Math.round((Number(amount) * fromRate / toRate + Number.EPSILON) * 100) / 100).toFixed(2);
};

const Dashboard = () => {
  return (
    <div className="space-y-10">
      <section>
        <h2 className="text-4xl font-semibold text-ink">Главная</h2>
      </section>

      <section className="grid gap-8 lg:grid-cols-[1fr_1.2fr]">
        <div className="rounded-xl3 border border-glass-border bg-glass px-6 py-7 backdrop-blur-lg shadow-glass">
          <BalanceWidget />
        </div>

        <div className="rounded-xl3 border border-glass-border bg-glass px-6 py-7 backdrop-blur-lg shadow-glass">
          <TransactionForm />
        </div>
      </section>
    </div>
  );
};

const TransactionForm = () => {
  const { triggerRefresh } = useBalance();
  const [formData, setFormData] = useState({
    amount: '',
    transfer_amount: '',
    description: '',
    type: 'expense',
    date: localNowDateTime(),
    category_id: '',
    income_source_id: '',
    from_location_id: '',
    to_location_id: '',
    tag_ids: []
  });
  const [categories, setCategories] = useState([]);
  const [incomeSources, setIncomeSources] = useState([]);
  const [locations, setLocations] = useState([]);
  const [tags, setTags] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [message, setMessage] = useState('');
  const [showSplitModal, setShowSplitModal] = useState(false);

  useEffect(() => {
    fetchReferenceData();
  }, []);

  const fetchReferenceData = async () => {
    try {
      const [categoriesRes, incomeSourcesRes, locationsRes, tagsRes, currenciesRes] = await Promise.all([
        apiClient.get('/categories'),
        apiClient.get('/income-sources'),
        apiClient.get('/locations'),
        apiClient.get('/tags'),
        apiClient.get('/currencies')
      ]);

      setCategories(categoriesRes.data.data || []);
      setIncomeSources(incomeSourcesRes.data.data || []);
      setLocations(locationsRes.data.data || []);
      setTags(tagsRes.data.data || []);
      setCurrencies(currenciesRes.data.data || []);
    } catch (err) {
      console.error('Error fetching reference data:', err);
      setError('Ошибка загрузки справочников');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFieldErrors((current) => clearTransactionFieldError(current, name));
    setFormData(prev => {
      const next = { ...prev, [name]: value };
      if (next.type === 'transfer' && (name === 'from_location_id' || name === 'to_location_id')) {
        const from = locations.find((location) => String(location.id) === String(next.from_location_id));
        const to = locations.find((location) => String(location.id) === String(next.to_location_id));
        const converted = getConvertedAmount(next.amount, from, to, currencies);
        if (converted) next.transfer_amount = converted;
      }
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    const validationErrors = transactionFieldErrors(formData);
    if (Object.keys(validationErrors).length) {
      setFieldErrors(validationErrors);
      setError('Заполните обязательные поля операции');
      return;
    }
    setLoading(true);

    try {
      const payload = {
        amount: parseFloat(formData.amount),
        description: formData.description,
        type: formData.type,
        date: formData.date,
        tag_ids: formData.tag_ids || []
      };

      if (formData.type === 'expense') {
        payload.category_id = parseInt(formData.category_id);
        payload.from_location_id = parseInt(formData.from_location_id);
      } else if (formData.type === 'income') {
        payload.income_source_id = parseInt(formData.income_source_id);
        payload.to_location_id = parseInt(formData.to_location_id);
      } else if (formData.type === 'transfer') {
        payload.from_location_id = parseInt(formData.from_location_id);
        payload.to_location_id = parseInt(formData.to_location_id);
        payload.transfer_amount = parseFloat(formData.transfer_amount);
      }

      await apiClient.post('/transactions', payload);
      triggerRefresh();
      setMessage('Операция добавлена');
      setFormData({
        amount: '',
        transfer_amount: '',
        description: '',
        type: 'expense',
        date: localNowDateTime(),
        category_id: '',
        income_source_id: '',
        from_location_id: '',
        to_location_id: '',
        tag_ids: []
      });
    } catch (err) {
      console.error('Error saving transaction:', err);
      const errorMessage = err.response?.data?.message || 'Ошибка при сохранении';
      setFieldErrors(fieldErrorFromTransactionResponse(errorMessage));
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const filteredCategories = categories;
  const fromLocation = locations.find((location) => String(location.id) === String(formData.from_location_id));
  const toLocation = locations.find((location) => String(location.id) === String(formData.to_location_id));
  const exchangeRate = Number(formData.amount) > 0 && Number(formData.transfer_amount) > 0
    ? Number(formData.transfer_amount) / Number(formData.amount)
    : null;
  const foreignCurrency = fromLocation?.currency === 'RUB' ? toLocation?.currency : fromLocation?.currency;
  const displayedRate = fromLocation?.currency === 'RUB' && toLocation?.currency !== 'RUB'
    ? 1 / exchangeRate
    : exchangeRate;

  return (
    <div>
      <div className="border-b border-line pb-4">
        <p className="text-xs uppercase tracking-[0.28em] text-muted">Новая операция</p>
        <h3 className="text-2xl font-semibold text-ink">Быстрое добавление</h3>
      </div>

      {(error || message) && (
        <div className={`mt-4 rounded-2xl border p-4 text-sm ${error ? 'border-red-500/40 bg-red-500/10 text-red-700 dark:text-red-300' : 'border-line bg-base text-ink'}`}>
          {error || message}
        </div>
      )}

      <form noValidate onSubmit={handleSubmit} className="mt-6 space-y-6">
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <Input
            label={formData.type === 'transfer' ? `Сумма списания${fromLocation ? `, ${fromLocation.currency}` : ''}` : 'Сумма'}
            type="number"
            name="amount"
            value={formData.amount}
            onChange={handleChange}
            required
            step="0.01"
            min="0"
            placeholder="0.00"
            error={fieldErrors.amount}
          />

          <Select
            label="Тип операции"
            name="type"
            value={formData.type}
            onChange={handleChange}
            required
          >
            <option value="expense">Расход</option>
            <option value="income">Доход</option>
            <option value="transfer">Перевод</option>
          </Select>
        </div>

        <DatePicker
          label="Дата"
          name="date"
          value={formData.date}
          onChange={handleChange}
          required
          error={fieldErrors.date}
        />

        {(formData.type === 'expense' || formData.type === 'transfer') && (
          <Select
            label="Откуда"
            name="from_location_id"
            value={formData.from_location_id}
            onChange={handleChange}
            required={formData.type === 'expense' || formData.type === 'transfer'}
            error={fieldErrors.from_location_id}
          >
            <option value="">Выберите счёт</option>
            {locations.map(location => (
              <option key={location.id} value={location.id}>{location.name} ({location.currency})</option>
            ))}
          </Select>
        )}

        {formData.type === 'expense' && (
          <CategoryPicker
            label="Категория расхода"
            name="category_id"
            value={formData.category_id}
            onChange={handleChange}
            categories={filteredCategories}
            placeholder="Выберите категорию"
            required
            error={fieldErrors.category_id}
          />
        )}

        {formData.type === 'income' && (
          <Select
            label="Источник дохода"
            name="income_source_id"
            value={formData.income_source_id}
            onChange={handleChange}
            required={formData.type === 'income'}
            error={fieldErrors.income_source_id}
          >
            <option value="">Выберите источник дохода</option>
            {incomeSources.map(source => (
              <option key={source.id} value={source.id}>{source.name}</option>
            ))}
          </Select>
        )}

        {(formData.type === 'income' || formData.type === 'transfer') && (
          <Select
            label="Куда"
            name="to_location_id"
            value={formData.to_location_id}
            onChange={handleChange}
            required={formData.type === 'income' || formData.type === 'transfer'}
            error={fieldErrors.to_location_id}
          >
            <option value="">Выберите счёт</option>
            {locations.map(location => (
              <option key={location.id} value={location.id}>{location.name} ({location.currency})</option>
            ))}
          </Select>
        )}

        {formData.type === 'transfer' && (
          <div className="space-y-2">
            <Input
              label={`Сумма зачисления${toLocation ? `, ${toLocation.currency}` : ''}`}
              type="number"
              name="transfer_amount"
              value={formData.transfer_amount}
              onChange={handleChange}
              required
              step="0.01"
              min="0.01"
              placeholder="0.00"
              className="hide-number-controls"
              error={fieldErrors.transfer_amount}
            />
            {exchangeRate && fromLocation && toLocation && fromLocation.currency !== toLocation.currency && (
              <p className="text-sm text-muted">Курс обмена: {fromLocation.currency === 'RUB' || toLocation.currency === 'RUB' ? `1 ${foreignCurrency} = ${displayedRate.toFixed(2)} RUB` : `1 ${fromLocation.currency} = ${displayedRate.toFixed(2)} ${toLocation.currency}`}</p>
            )}
          </div>
        )}

        <Input
          label="Описание"
          type="text"
          name="description"
          value={formData.description}
          onChange={handleChange}
          placeholder="Введите описание транзакции"
        />

        <div>
          <label className="mb-2 block text-xs uppercase tracking-[0.24em] text-muted">Теги</label>
          <div className="mb-3 flex items-center gap-3 text-xs uppercase tracking-[0.22em] text-muted">
            <button
              type="button"
              className="border-b border-line"
              onClick={() => setFormData(prev => ({ ...prev, tag_ids: tags?.map(tag => tag.id) || [] }))}
              disabled={!tags?.length}
            >
              Выбрать все
            </button>
            <span>/</span>
            <button
              type="button"
              className="border-b border-line"
              onClick={() => setFormData(prev => ({ ...prev, tag_ids: [] }))}
              disabled={!tags?.length}
            >
              Снять все
            </button>
          </div>
          <div className="max-h-48 space-y-2 overflow-y-auto rounded-2xl border border-line bg-surface p-3">
            {!tags?.length && (
              <p className="text-xs text-muted">Нет тегов</p>
            )}
            {tags?.map(tag => {
              const checked = formData.tag_ids.includes(tag.id);
              return (
                <label
                  key={tag.id}
                  className="flex items-center gap-3 text-sm text-ink-subtle"
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => {
                      setFormData(prev => {
                        const exists = prev.tag_ids.includes(tag.id);
                        return {
                          ...prev,
                          tag_ids: exists
                            ? prev.tag_ids.filter(id => id !== tag.id)
                            : [...prev.tag_ids, tag.id]
                        };
                      });
                    }}
                    className="h-4 w-4 rounded border-line text-accent focus:ring-accent"
                  />
                  {tag.name}
                </label>
              );
            })}
          </div>
        </div>

        <div className="flex flex-col gap-3 sm:flex-row sm:justify-end">
          <Button type="button" variant="secondary" onClick={() => setShowSplitModal(true)}>
            Разбить
          </Button>
          <Button type="submit" variant="primary" loading={loading}>
            Добавить
          </Button>
        </div>
      </form>

      <TransactionModal
        isOpen={showSplitModal}
        onClose={() => setShowSplitModal(false)}
        categories={categories}
        incomeSources={incomeSources}
        locations={locations}
        tags={tags}
        onSave={() => {
          setShowSplitModal(false);
          triggerRefresh();
          setMessage('Операция с разбивкой добавлена');
        }}
        startWithSplit
      />
    </div>
  );
};

export default Dashboard;
